<template>
  <!-- 填充 -->
  <AttrImage
    label="预览图"
    class="mt-2.5"
    :base64="preview"
    :disabled="attrDisabled == 'not-image'"
    @change="(image: string) => onImageUpdate(image,'jsPreview')"
    @delete="() => onImageDelete('jsPreview')"
  />

  <UploadDirectory
    label="上传文件夹"
    class="mt-2.5"
    :dirname="dirname"
    :disabled="!!attrDisabled"
    :show-tooltip="true"
    @upload="onUploadDir"
    @delete="onDeleteDir"
  />
  <AttrSelect
    class="mt-2.5"
    label="引用方式"
    :value="fileRefType"
    :options="refOptions"
    :disabled="!!attrDisabled"
    @change="setFileRef"
  />

  <!-- 图片输出格式 -->
  <div>
    <AttrImgFomat :shape="shape" />
  </div>
</template>

<script lang="ts" setup>
import { PropType, computed } from 'vue';
import { Shape } from '@/services/shape';
import { ResourceKey } from '@/services/shape/attributes';
import { ElectronApi } from '@/services/electron.api';
import { useEditorStore } from '@/store/use-editor-store';
import { pickDir } from '@/utils/fs/pick-file.utils';
import { WatchfaceService } from '@/services/watchface';
import { Modal } from 'ant-design-vue';
import { emptyDir, isDirectory, isFile } from '@/utils/fs/fs-extra.utils';
import { JsFileAttribute } from '@/services/shape/attributes/js-file';

const editorStore = useEditorStore();
const attrDisabled = computed(() => editorStore.attrDisabled);

const refOptions = ['widget引用', 'layout引用'].map((item) => {
  return {
    label: item,
    value: item
  };
});

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  }
});

const fileRefType = computed(() => props.shape.attrs.fileRef ?? 'widget引用');
const preview = computed(() => {
  const _preview = props.shape.getResources({
    resourceName: 'jsPreview',
    colorGroup: editorStore.themeColor?.id ?? '',
    base64: true
  });
  return _preview?.[0] ?? '';
});

const onImageUpdate = async (image: string, resourceName: ResourceKey) => {
  props.shape?.uploadResource(
    {
      resourceName,
      colorGroup: editorStore.themeColor?.id ?? '',
      realpath: image
    },
    true
  );
};

const onImageDelete = async (resourceName: ResourceKey) => {
  props.shape?.deleteResource({
    resourceName,
    colorGroup: editorStore.themeColor?.id ?? ''
  });
};

const onDeleteDir = async () => {
  let dir = dirname.value;
  dir = ElectronApi.path?.join(new WatchfaceService().ResourcePath, dir ?? '');
  if (dir) {
    await ElectronApi.fs?.promises.rm(dir, { recursive: true, force: true });
  }

  props.shape.setAttrs({
    jsFilename: '',
    jsImgNames: []
  });
};

const dirname = computed(() => {
  const parts = props.shape.attrs.jsFilename?.split('/') ?? [];
  parts.pop();
  return parts.pop();
});

const onUploadDir = async () => {
  const fs = ElectronApi.fs;
  const path = ElectronApi.path;
  const dir = (await pickDir()) as string | undefined;
  const folder = new WatchfaceService();
  // 未选择时，不可添加
  if (!dir || !fs || !path) return;
  // 获取js或lua文件
  let _file = (await fs.promises.readdir(dir)).find((item) => {
    const _item = item.toLowerCase();
    return _item.endsWith('.js') || _item.endsWith('.lua');
  });
  if (!_file) {
    Modal.warning({
      title: '无法上传',
      content: '请确保目录下有且只有一个js或lua文件！',
      okText: '知道了'
    });
    return;
  }
  const isJs = _file.endsWith('.js');
  const isLua = _file.endsWith('.lua');
  const _jsDir = isJs ? '_js' : '_lua';

  // 将用户选择的文件全部复制
  const _dirname = path.basename(dir);
  const dest = path.join(folder.ResourcePath, _jsDir, _dirname);
  await emptyDir(dest);
  await fs.promises.cp(dir, dest, {
    recursive: true,
    force: true
  });
  // 读取所有文件
  const jsFileImgs: string[] = [];
  const filenames = await fs.promises.readdir(dest);
  const isLuaTxtOrImg = (filename: string) =>
    filename.endsWith('.txt') ||
    filename.endsWith('.rle') ||
    filename.endsWith('.bin') ||
    filename.endsWith('.png');
  for (const filename of filenames) {
    if (isJs) {
      const jsImgFormats = [
        'argb8888',
        'xrgb8888',
        'alpha8',
        'indexed8',
        'rgb565',
        'argb4444'
      ];
      if (jsImgFormats.includes(filename.toLowerCase())) {
        const dest = path.join(dir, filename);
        const imgDir = await fs.promises.readdir(dest);
        imgDir.forEach((imgName) => {
          if (imgName.endsWith('.png'))
            jsFileImgs.push([_jsDir, _dirname, filename, imgName].join('/'));
        });
      }
    }
    if (isLua) {
      const _filename = path.join(dest, filename);
      if (await isFile(_filename)) {
        if (isLuaTxtOrImg(filename))
          jsFileImgs.push([_jsDir, _dirname, filename].join('/'));
      }
      if (await isDirectory(_filename)) {
        const files = await fs.promises.readdir(_filename);
        files.forEach((file) => {
          if (isLuaTxtOrImg(file)) {
            jsFileImgs.push([_jsDir, _dirname, filename, file].join('/'));
          }
        });
      }
    }
  }
  props.shape.setAttrs({
    jsFilename: [_jsDir, _dirname, _file].join('/'),
    jsImgNames: jsFileImgs
  });
};

const setFileRef = (val: JsFileAttribute['fileRef']) => {
  props.shape.setAttrs({ fileRef: val });
};
</script>
